/**
 * useMiraiYochi Hook - Phase 4-2
 *
 * 【未来予知】キーワード能力を管理するカスタムフック。
 * デッキトップを確認し、上か下に戻すかを選択する。
 *
 * 特徴:
 * - useState 1個（pendingMiraiYochi）
 * - メインフェイズ中のみ発動可能
 */

import { useState, useCallback } from 'react';
import type React from 'react';
import { canActivateMiraiYochi } from '../../engine/keywordAbilities';
import type { MonsterInstance, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { PendingDeckReview, PendingMiraiYochi } from '../../types/ui';

interface UseMiraiYochiProps {
  currentPlayer: PlayerNumber;
  phase: number;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setPendingDeckReview: React.Dispatch<React.SetStateAction<PendingDeckReview | null>>;
}

interface UseMiraiYochiReturn {
  pendingMiraiYochi: PendingMiraiYochi | null;
  setPendingMiraiYochi: React.Dispatch<React.SetStateAction<PendingMiraiYochi | null>>;
  activateMiraiYochi: (monsterIndex: number) => void;
  completeMiraiYochi: (monsterIndex: number, position: 'top' | 'bottom') => void;
  cancelMiraiYochi: () => void;
}

export const useMiraiYochi = ({
  currentPlayer,
  phase,
  p1Field,
  p2Field,
  p1Deck,
  p2Deck,
  addLog,
  setP1Field,
  setP2Field,
  setP1Deck,
  setP2Deck,
  setPendingDeckReview,
}: UseMiraiYochiProps): UseMiraiYochiReturn => {
  // 【未来予知】発動中の状態
  const [pendingMiraiYochi, setPendingMiraiYochi] = useState<PendingMiraiYochi | null>(null);
  // pendingMiraiYochi = { monsterSlotIndex, card } - 発動元モンスター情報

  /**
   * 【未来予知】発動
   */
  const activateMiraiYochi = useCallback((monsterIndex: number): void => {
    const myField = currentPlayer === 1 ? p1Field : p2Field;
    const myDeck = currentPlayer === 1 ? p1Deck : p2Deck;
    const monster = myField[monsterIndex];

    if (!monster || !canActivateMiraiYochi(monster, phase)) {
      addLog('【未来予知】発動条件を満たしていません', 'damage');
      return;
    }

    if (myDeck.length === 0) {
      addLog('【未来予知】デッキにカードがありません', 'damage');
      return;
    }

    // デッキトップのカードを取得
    const topCard = myDeck[0];

    // 発動元モンスターを記録
    setPendingMiraiYochi({ monsterSlotIndex: monsterIndex, card: monster });

    // デッキトップ確認UIを表示（上/下選択）
    setPendingDeckReview({
      cards: [topCard],
      title: '【未来予知】',
      message: `${monster.name}の効果でデッキトップを確認。このカードをデッキの上に戻すか、下に戻すか選択してください。`,
      allowReorder: false,
      selectMode: 'miraiYochi', // カスタムモード
      onSelect: (position) => {
        completeMiraiYochi(monsterIndex, position);
      },
      onCancel: () => {
        setPendingMiraiYochi(null);
        setPendingDeckReview(null);
        addLog('【未来予知】キャンセルしました', 'info');
      }
    });

    addLog(`${monster.name}が【未来予知】を発動！`, 'info');
  }, [currentPlayer, p1Field, p2Field, p1Deck, p2Deck, phase, addLog, setPendingDeckReview]);

  /**
   * 【未来予知】完了（上/下選択後）
   */
  const completeMiraiYochi = useCallback((monsterIndex: number, position: 'top' | 'bottom'): void => {
    const setMyField = currentPlayer === 1 ? setP1Field : setP2Field;
    const myDeck = currentPlayer === 1 ? p1Deck : p2Deck;
    const setMyDeck = currentPlayer === 1 ? setP1Deck : setP2Deck;
    const myField = currentPlayer === 1 ? p1Field : p2Field;
    const monster = myField[monsterIndex];

    if (myDeck.length === 0) {
      setPendingMiraiYochi(null);
      setPendingDeckReview(null);
      return;
    }

    const topCard = myDeck[0];

    if (position === 'top') {
      // デッキの上に戻す（そのまま）
      addLog(`【未来予知】${topCard.name}をデッキの上に戻した`, 'info');
    } else if (position === 'bottom') {
      // デッキの下に戻す
      setMyDeck(prev => {
        const newDeck = [...prev];
        const card = newDeck.shift(); // トップを取り出す
        if (card) {
          newDeck.push(card); // ボトムに追加
        }
        return newDeck;
      });
      addLog(`【未来予知】${topCard.name}をデッキの下に戻した`, 'info');
    }

    // モンスターに使用済みフラグを設定
    setMyField(prev => {
      const newField = [...prev];
      if (newField[monsterIndex]) {
        newField[monsterIndex] = { ...newField[monsterIndex], usedMiraiYochiThisTurn: true };
      }
      return newField;
    });

    setPendingMiraiYochi(null);
    setPendingDeckReview(null);
  }, [currentPlayer, p1Field, p2Field, p1Deck, p2Deck, setP1Field, setP2Field, setP1Deck, setP2Deck, addLog, setPendingDeckReview]);

  /**
   * 【未来予知】キャンセル
   */
  const cancelMiraiYochi = useCallback((): void => {
    setPendingMiraiYochi(null);
    setPendingDeckReview(null);
  }, [setPendingDeckReview]);

  return {
    // State
    pendingMiraiYochi,
    setPendingMiraiYochi,

    // Actions
    activateMiraiYochi,
    completeMiraiYochi,
    cancelMiraiYochi,
  };
};

export default useMiraiYochi;
